import {
  AccessTags,
  ChartTags,
  Market,
  MarketTags,
  PageTags,
  getAccessTags,
  getMarketTagsOrDefault,
  getPageTagsOrDefault,
  useAnalytics,
} from '@ecosystem/newron-client-lib';
import { useCallback } from 'react';

import { getUniqueValues } from '../../private/components/MultiEdit/utils';
import {
  AccessLevel,
  Collection,
  CollectionDataType,
  CollectionInsight,
  CollectionListItem,
  GetTrackingTagsFn,
  InsightErrorType,
  InsightInfo,
  InsightTag,
  Proposition,
  SelectedCollectionOption,
} from '../types';

import { mapInsightInfo } from './helpers';
import {
  AddInsightToCollectionTags,
  CollectionChartInteractionsTags,
  CollectionDetailsPageViewedTags,
  CollectionInteractionsTags,
  CollectionsDetailsPageTags,
  CollectionsModalInfoTags,
  CollectionsOverviewPageTags,
  CollectionsPageViewedTags,
  CollectionsTableClickedTags,
  CreateCollectionTags,
  EditInsightSettingsTags,
  ExpandOrCollapseTags,
  InsightExportConfirmationTags,
  InsightReorderedTags,
  InsightViewedTags,
  InsightWithErrorsTags,
  OpenAddInsightToCollectionModalTags,
  OpenInsightExportModalTags,
  QuickActionsInteractionsTags,
  TextBlockAddedToCollectionTags,
  TextBlockInteractionsTags,
  TextBlockViewedTags,
  ToCInsightCardClickedTags,
  ToCToggleTags,
} from './types';

interface CollectionPageAnalytics {
  numberOfCollections: number;
}

interface CollectionClickedAnalytics extends CollectionPageAnalytics {
  collection: Pick<CollectionListItem, 'id' | 'accessLevel' | 'sharedWithCount' | 'lastViewedAt'>;
  optionChosen: string;
  position: number;
  searchActive: boolean;
  filteredCollections: number;
}
interface QuickActionsClickedAnalytics {
  elementName: QuickActionsInteractionsTags['elementName'];
  elementType?: QuickActionsInteractionsTags['elementType'];
  optionChosen?: string;
  collectionId?: string;
  originalCollectionName?: string;
  newCollectionName?: string;
  newCollectionId?: string;
}
interface CollectionDetailsPageAnalytics {
  collection: Collection<unknown>;
}

interface CollectionDetailsElementClickedAnalytics {
  elementName: CollectionInteractionsTags['elementName'];
  collection: Partial<Pick<Collection<unknown>, 'id' | 'name' | 'accessLevel' | 'sharedWithCount'>>;
  collectionDescriptionEdited?: boolean;
  newCollectionId?: string;
  newCollectionName?: string;
  numberOfInsights?: string;
  sharedAdded?: number;
  sharedRemoved?: number;
  changedProperty?: string;
  optionChosen?: Record<string, string>;
  updatedInsights?: CollectionInsight<unknown>[];
}

interface CreateCollectionAnalytics {
  collectionName: string;
  collectionId: string;
}

interface InsightElementClickedAnalytics {
  elementName: ExpandOrCollapseTags['elementName'];
  elementCategory: ExpandOrCollapseTags['elementCategory'];
  collectionName: string;
  insightName: string;
  trackingTags: Partial<AccessTags>;
}

interface InsightViewedAnalytics {
  collectionName: string;
  insight: CollectionInsight<unknown>;
  insightPosition: number;
  insightInfo: InsightInfo;
}

interface TextBlockAnalytics {
  collectionName: string;
  insight: Pick<CollectionInsight<unknown>, 'name' | 'description'>;
  insightPosition: number;
}

interface TextBlockInteractionsAnalytics extends TextBlockAnalytics {
  elementName: TextBlockInteractionsTags['elementName'];
  insightDescriptionEdited?: boolean;
}

interface InsightWithErrorsAnalytics {
  collectionName: string;
  insight: CollectionInsight<unknown>;
  insightInfo: InsightInfo;
  insightPosition: number;
  error: InsightErrorType;
}

interface InsightPositionAnalytics {
  insightPosition: number;
}

interface InsightConfigItemsChanges extends InsightPositionAnalytics {
  collectionId: string;
  dataType: CollectionDataType;
  configItems: InsightTag[];
}

interface CollectionChartInteractionsAnalytics {
  elementName: CollectionChartInteractionsTags['elementName'];
  collectionName: string;
  chartName: string;
  dataType: string;
  insightName: string;
  insightDescriptionEdited?: boolean;
  insightPosition: number;
  trackingTags: Partial<AccessTags & MarketTags & ChartTags>;
  source?: string;
  proposition?: Proposition;
}

interface AddInsightToCollectionButtonClickedAnalytics {
  getTrackingTags?: GetTrackingTagsFn;
  insightInfo: InsightInfo;
}

interface AddInsightToCollectionAnalytics {
  insightName: string;
  insightInfo: InsightInfo;
  insightDescriptionAdded: boolean;
  dataType: string;
  selectedCollections: SelectedCollectionOption[];
  getTrackingTags?: GetTrackingTagsFn;
}

interface OpenInsightExportModalAnalytics {
  chartName: string;
  source: string;
  collectionName: string;
  trackingTags: Partial<AccessTags & ChartTags>;
  proposition: Proposition;
}

interface ExportedInsightAnalytics extends OpenInsightExportModalAnalytics {
  optionChosen: string;
}

interface InsightAnalytics {
  collectionName: string;
  insightName: string;
  source?: string;
  proposition?: Proposition;
  chartType?: ChartTags['chartType'];
}

interface ToCInsightCardClickedAnalytics {
  collectionName: string;
  wasExpanded: boolean;
}

interface EditInsightSettingsAnalitycs
  extends Omit<InsightAnalytics, 'chartType'>,
    Omit<InsightConfigItemsChanges, 'configItems'> {
  trackingTags: Partial<AccessTags & ChartTags>;
  chartName: string;
}
interface UseCollectionAnalyticsReturnType {
  trackCollectionsPageViewed: (eventProps: CollectionPageAnalytics) => Promise<void>;
  trackCollectionDetailsPageViewed: (eventProps: CollectionDetailsPageAnalytics) => Promise<void>;
  trackCollectionClicked: (eventProps: CollectionClickedAnalytics) => Promise<void>;
  trackCollectionDetailsPageElementClicked: (eventProps: CollectionDetailsElementClickedAnalytics) => Promise<void>;
  trackCreateCollectionPageElementClicked: (eventProps: CreateCollectionAnalytics) => Promise<void>;
  trackCollectionDetailsPageChartInteractions: (eventProps: CollectionChartInteractionsAnalytics) => void;
  trackInsightElementClicked: (eventProps: InsightElementClickedAnalytics) => void;
  trackInsightViewed: (eventProps: InsightViewedAnalytics) => void;
  trackTextBlockViewed: (eventProps: TextBlockAnalytics) => void;
  trackTextBlockInteractions: (eventProps: TextBlockInteractionsAnalytics) => void;
  trackInsightError: (eventProps: InsightWithErrorsAnalytics) => void;
  trackAddInsightToCollectionButtonClicked: (eventProps: AddInsightToCollectionButtonClickedAnalytics) => void;
  trackInsightAddedToCollection: (eventProps: AddInsightToCollectionAnalytics) => void;
  trackTextBlockAddedToCollection: (eventProps: TextBlockAnalytics) => void;
  trackCollectionsInfoModalClicked: () => void;
  trackInsightExportButtonClicked: (eventProps: OpenInsightExportModalAnalytics) => void;
  trackInsightExportConfirmationButtonClicked: (eventProps: ExportedInsightAnalytics) => void;
  trackToCInsightCardClicked: (eventProps: InsightAnalytics) => void;
  trackInsightReordered: (eventProps: InsightAnalytics) => void;
  trackQuickActionsMenuItemClicked: (eventProps: QuickActionsClickedAnalytics) => Promise<void>;
  trackToCToggle: (eventProps: ToCInsightCardClickedAnalytics) => void;
  trackInsightLinkClicked: (eventProps: InsightAnalytics & InsightPositionAnalytics) => void;
  trackInsightConfigItemsApplied: (eventProps: InsightAnalytics & InsightConfigItemsChanges) => void;
  trackEmptyCollectionsLinkClicked: () => void;
  trackEditInsightSettingsButtonClicked: (eventProps: EditInsightSettingsAnalitycs) => void;
  trackSearchCollections: () => void;
}

const isShared = (collection: Pick<Collection<unknown>, 'accessLevel' | 'sharedWithCount'>): boolean =>
  collection?.accessLevel === AccessLevel.Viewer ||
  (collection?.sharedWithCount !== undefined && collection?.sharedWithCount > 0);

const collectionsOverviewPage: CollectionsOverviewPageTags = {
  section: 'Collections',
  subsection: 'Collections Overview',
  pageName: 'Collections Overview',
};

const collectionsDetailsPage: CollectionsDetailsPageTags = {
  section: 'Collections',
  subsection: 'Collections Details',
  pageName: 'Collections Details',
};

export const useCollectionAnalytics = (): UseCollectionAnalyticsReturnType => {
  const { track } = useAnalytics();

  const trackCollectionsPageViewed = useCallback(
    async (eventProps: CollectionPageAnalytics) => {
      track<CollectionsPageViewedTags>('Page Viewed', {
        ...(await getAccessTags()),
        ...collectionsOverviewPage,
        elementType: 'Collections',
        numberOfCollections: eventProps.numberOfCollections,
      });
    },
    [track]
  );

  const trackCollectionDetailsPageViewed = useCallback(
    async (eventProps: CollectionDetailsPageAnalytics) => {
      const collection = eventProps.collection;
      const propositions = collection.insights?.map(i => i.proposition);
      const dataTypes = collection.insights?.map(i => i.dataType);

      track<CollectionDetailsPageViewedTags>('Page Viewed', {
        ...(await getAccessTags()),
        ...collectionsDetailsPage,
        elementName: 'View Collection details',
        elementType: 'Collections',
        collectionId: collection.id,
        collectionName: collection.name,
        numberOfInsights: collection.insights?.length,
        shared: isShared(collection),
        rights: collection.accessLevel === AccessLevel.Viewer ? 'Viewer' : 'Owner',
        proposition: propositions?.filter((value, index, array) => array.indexOf(value) === index).join(','),
        dataType: dataTypes?.filter((value, index, array) => array.indexOf(value) === index).join(','),
      });
    },
    [track]
  );

  const trackInsightViewed = useCallback(
    (eventProps: InsightViewedAnalytics) => {
      const { access, chartType, ...rest } = eventProps.insightInfo.trackingTags ?? {};

      track<InsightViewedTags>('Page Element Viewed', {
        access: access ?? '',
        ...collectionsDetailsPage,
        elementName: 'View Collection details - Insight',
        elementType: 'Collections',
        elementCategory: 'Chart',
        collectionName: eventProps.collectionName,
        dataType: eventProps.insight?.dataType,
        chartName: eventProps.insightInfo.chartName ?? '',
        chartType: chartType ?? '',
        insightDescriptionAdded: !!eventProps.insight?.description?.length,
        insightPosition: eventProps.insightPosition,
        ...mapInsightInfo(eventProps.insightInfo),
        ...rest,
        proposition: eventProps.insight.proposition,
        source: eventProps.insightInfo.chartName,
      });
    },
    [track]
  );

  const trackTextBlockViewed = useCallback(
    async (eventProps: TextBlockAnalytics) => {
      track<TextBlockViewedTags>('Page Element Viewed', {
        ...(await getAccessTags()),
        ...collectionsDetailsPage,
        elementName: 'View Collection details - Text Block',
        elementType: 'Collections',
        elementCategory: 'TextBlock',
        proposition: Proposition.Platform,
        collectionName: eventProps.collectionName,
        insightName: eventProps.insight?.name,
        insightDescriptionAdded: !!eventProps.insight.description?.length,
        insightPosition: eventProps.insightPosition,
      });
    },
    [track]
  );

  const mapErrorType = (error: InsightErrorType): string => {
    const errorTypes: Record<InsightErrorType, string> = {
      [InsightErrorType.RENDER_ERROR]: 'Render',
      [InsightErrorType.SUBSCRIPTION_ERROR]: 'Subscription',
      [InsightErrorType.BFF_ERROR]: 'Server down',
      [InsightErrorType.EXPIRED]: 'Expiration',
      [InsightErrorType.MULTI_CONFIG_ERROR]: 'Multi Config',
      [InsightErrorType.DATA_NOT_AVAILABLE]: 'Data Not Available',
      [InsightErrorType.OTHER]: 'Other',
    };

    return errorTypes[error] || 'Unknown';
  };

  const trackInsightError = useCallback(
    (eventProps: InsightWithErrorsAnalytics) => {
      const { access, chartType } = eventProps.insightInfo?.trackingTags ?? {};

      track<InsightWithErrorsTags>('Page Element Viewed', {
        access: access ?? '',
        ...collectionsDetailsPage,
        elementName: eventProps.insight?.name,
        elementType: 'Collections',
        elementCategory: 'Chart',
        collectionName: eventProps.collectionName,
        dataType: eventProps.insight?.dataType ?? '',
        chartName: eventProps.insightInfo?.chartName ?? '',
        chartType: chartType ?? '',
        insightDescriptionAdded: !!eventProps.insight?.description?.length,
        insightPosition: eventProps.insightPosition,
        ...mapInsightInfo(eventProps.insightInfo),
        error: mapErrorType(eventProps.error),
      });
    },
    [track]
  );

  const trackCollectionClicked = useCallback(
    async (eventProps: CollectionClickedAnalytics) => {
      const { collection } = eventProps;

      track<CollectionsTableClickedTags>('Table Clicked', {
        ...(await getAccessTags()),
        ...collectionsOverviewPage,
        tableName: 'Collections',
        elementName: 'Collection Name Link Clicked',
        elementType: 'Collections',
        position: eventProps.position,
        optionChosen: eventProps.optionChosen,
        numberOfCollections: eventProps.numberOfCollections,
        searchActive: eventProps.searchActive,
        filteredCollections: eventProps.filteredCollections,
        collectionId: collection.id,
        shared: isShared(collection),
        rights: collection.accessLevel === AccessLevel.Viewer ? 'Viewer' : 'Owner',
        new: !collection.lastViewedAt,
      });
    },
    [track]
  );

  const trackQuickActionsMenuItemClicked = useCallback(
    async (eventProps: QuickActionsClickedAnalytics) => {
      const { collectionId, ...rest } = eventProps;
      const trackingTags: QuickActionsInteractionsTags = {
        ...(await getAccessTags()),
        ...collectionsOverviewPage,
        tableName: 'Collections',
        elementType: 'Collections',
        elementCategory: 'CTA',
        column: 'Context menu',
        ...rest,
      };
      if (eventProps.elementName === 'Duplicate Collection' && collectionId) {
        trackingTags.originalCollectionId = collectionId;
      }
      track<QuickActionsInteractionsTags>('Table Clicked', trackingTags);
    },
    [track]
  );

  const trackCollectionDetailsPageElementClicked = useCallback(
    async (eventProps: CollectionDetailsElementClickedAnalytics) => {
      const { collection, updatedInsights, newCollectionId, newCollectionName, ...rest } = eventProps;
      const trackingProps: CollectionInteractionsTags = {
        ...(await getAccessTags()),
        ...collectionsDetailsPage,
        elementType: 'Collections',
        elementCategory: 'Button',
        collectionId: collection?.id,
        collectionName: collection?.name,
        shared: isShared(collection),
        ...(collection?.sharedWithCount !== undefined && { sharedWithCount: collection?.sharedWithCount }),
        rights: collection?.accessLevel === AccessLevel.Viewer ? 'Viewer' : 'Owner',
        ...(updatedInsights && {
          numberChangedInsights: updatedInsights.length,
          propositionChanged: getUniqueValues(updatedInsights.map(i => i.proposition)),
        }),
        ...rest,
      };
      if (eventProps.elementName === 'Duplicate Collection') {
        delete trackingProps.collectionId;
        delete trackingProps.collectionName;
        trackingProps.originalCollectionId = collection?.id;
        trackingProps.originalCollectionName = collection?.name;
        trackingProps.newCollectionId = newCollectionId;
        trackingProps.newCollectionName = newCollectionName;
      }
      track<CollectionInteractionsTags>('Page Element Clicked', trackingProps);
    },
    [track]
  );

  const trackCreateCollectionPageElementClicked = useCallback(
    async (eventProps: CreateCollectionAnalytics) => {
      track<CreateCollectionTags>('Page Element Clicked', {
        ...(await getAccessTags()),
        ...collectionsOverviewPage,
        elementName: 'Create Collection',
        elementType: 'Collections',
        elementCategory: 'Button',
        ...eventProps,
      });
    },
    [track]
  );

  const trackTextBlockAddedToCollection = useCallback(
    async (eventProps: TextBlockAnalytics) => {
      track<TextBlockAddedToCollectionTags>('Page Element Clicked', {
        ...(await getAccessTags()),
        ...collectionsDetailsPage,
        elementName: 'Add Text Block',
        elementType: 'Collections',
        elementCategory: 'TextBlock',
        proposition: Proposition.Platform,
        collectionName: eventProps.collectionName,
        insightName: eventProps.insight?.name,
        insightDescriptionAdded: !!eventProps.insight?.description?.length,
        insightPosition: eventProps.insightPosition,
      });
    },
    [track]
  );

  const trackInsightElementClicked = useCallback(
    (eventProps: InsightElementClickedAnalytics) => {
      const { access, ...rest } = eventProps.trackingTags ?? {};
      track<ExpandOrCollapseTags>('Page Element Clicked', {
        ...collectionsDetailsPage,
        elementName: eventProps.elementName,
        elementType: 'Collections',
        elementCategory: eventProps.elementCategory,
        insightName: eventProps.insightName,
        collectionName: eventProps.collectionName,
        access: access ?? '',
        ...rest,
      });
    },
    [track]
  );

  const trackCollectionDetailsPageChartInteractions = useCallback(
    (eventProps: CollectionChartInteractionsAnalytics) => {
      const { access, locationId, itemGroup, chartType } = eventProps.trackingTags ?? {};
      track<CollectionChartInteractionsTags>('Chart Interactions', {
        ...collectionsDetailsPage,
        elementName: eventProps.elementName,
        elementType: 'Collections',
        collectionName: eventProps.collectionName,
        chartName: eventProps.chartName ?? '',
        source: eventProps.source,
        proposition: eventProps.proposition,
        dataType: eventProps.dataType,
        insightName: eventProps.insightName,
        insightDescriptionEdited: eventProps.insightDescriptionEdited,
        insightPosition: eventProps.insightPosition,
        access: access ?? '',
        locationId: locationId ?? '',
        itemGroup: itemGroup ?? '',
        chartType: chartType ?? '',
        ...collectionsDetailsPage,
      });
    },
    [track]
  );

  const trackTextBlockInteractions = useCallback(
    async (eventProps: TextBlockInteractionsAnalytics) => {
      track<TextBlockInteractionsTags>('Page Element Clicked', {
        ...(await getAccessTags()),
        ...collectionsDetailsPage,
        elementName: eventProps.elementName,
        elementType: 'Collections',
        elementCategory: 'TextBlock',
        proposition: Proposition.Platform,
        collectionName: eventProps.collectionName,
        insightName: eventProps.insight?.name,
        insightDescriptionEdited: eventProps.insightDescriptionEdited,
        insightPosition: eventProps.insightPosition,
      });
    },
    [track]
  );

  const trackAddInsightToCollectionButtonClicked = useCallback(
    (eventProps: AddInsightToCollectionButtonClickedAnalytics) => {
      const { getTrackingTags, insightInfo } = eventProps;
      const { chartName } = insightInfo;
      const { access, chartType } = insightInfo.trackingTags ?? {};

      const trackingTags = getTrackingTags?.() ?? {
        itemGroup: '',
        locationId: '',
        pageName: '',
        section: '',
        subsection: '',
      };
      const pageTags = getPageTagsOrDefault(trackingTags as Partial<PageTags>);
      const marketTags = getMarketTagsOrDefault(trackingTags as Partial<Market>);
      track<OpenAddInsightToCollectionModalTags>('Chart Interactions', {
        ...trackingTags,
        access: access ?? '',
        chartName: chartName ?? '',
        chartType: chartType ?? '',
        elementName: 'Add to Collections button clicked',
        elementType: 'Collections',
        ...pageTags,
        ...marketTags,
        ...mapInsightInfo(insightInfo),
      });
    },
    [track]
  );

  const trackInsightAddedToCollection = useCallback(
    (eventProps: AddInsightToCollectionAnalytics) => {
      const { selectedCollections, getTrackingTags, insightInfo, ...rest } = eventProps;
      const { access, chartType } = insightInfo.trackingTags ?? {};

      const trackingTags = getTrackingTags?.() ?? {
        itemGroup: '',
        locationId: '',
        pageName: '',
        section: '',
        subsection: '',
      };
      const pageTags = getPageTagsOrDefault(trackingTags as Partial<PageTags>);
      const marketTags = getMarketTagsOrDefault(trackingTags as Partial<Market>);

      const collectionCreated =
        eventProps.selectedCollections
          ?.filter(c => !c.id)
          .map(c => c.name)
          .join(',') ?? '';

      const collectionSelected =
        eventProps.selectedCollections
          ?.filter(c => c.id)
          .map(c => c.name)
          .join(',') ?? '';

      track<AddInsightToCollectionTags>('Chart Interactions', {
        access: access ?? '',
        chartName: insightInfo.chartName ?? '',
        chartType: chartType ?? '',
        elementName: 'Collection Added',
        elementType: 'Collections',
        collectionCreated,
        collectionSelected,
        ...rest,
        ...trackingTags,
        ...pageTags,
        ...marketTags,
        ...mapInsightInfo(insightInfo),
      });
    },
    [track]
  );

  const trackCollectionsInfoModalClicked = useCallback(async () => {
    track<CollectionsModalInfoTags>('Page Element Clicked', {
      ...(await getAccessTags()),
      elementType: 'Collections',
      elementName: 'Help icon:Collections',
      elementCategory: 'Help',
      ...collectionsOverviewPage,
    });
  }, [track]);

  const trackInsightExportButtonClicked = useCallback(
    (eventProps: OpenInsightExportModalAnalytics) => {
      const { access, chartType } = eventProps.trackingTags ?? {};

      track<OpenInsightExportModalTags>('Data Export Initiated', {
        ...collectionsDetailsPage,
        elementType: 'Collections',
        proposition: eventProps.proposition,
        collectionName: eventProps.collectionName,
        chartName: eventProps.chartName,
        source: eventProps.source,
        access: access ?? '',
        chartType: chartType ?? '',
      });
    },
    [track]
  );

  const trackInsightExportConfirmationButtonClicked = useCallback(
    (eventProps: ExportedInsightAnalytics) => {
      const { access, chartType } = eventProps.trackingTags ?? {};
      track<InsightExportConfirmationTags>('Data Exported', {
        ...collectionsDetailsPage,
        elementType: 'Collections',
        collectionName: eventProps.collectionName,
        proposition: eventProps.proposition,
        chartName: eventProps.chartName,
        source: eventProps.source,
        optionChosen: eventProps.optionChosen,
        access: access ?? '',
        chartType: chartType ?? '',
      });
    },
    [track]
  );

  const trackToCInsightCardClicked = useCallback(
    async (eventProps: InsightAnalytics) => {
      track<ToCInsightCardClickedTags>('Page Element Clicked', {
        ...(await getAccessTags()),
        ...collectionsDetailsPage,
        elementType: 'Collections',
        elementCategory: 'Table of Contents',
        elementName: 'Insights Card clicked',
        collectionName: eventProps.collectionName,
        insightName: eventProps.insightName,
      });
    },
    [track]
  );

  const trackInsightReordered = useCallback(
    async (eventProps: InsightAnalytics) => {
      track<InsightReorderedTags>('Page Element Clicked', {
        ...(await getAccessTags()),
        ...collectionsDetailsPage,
        elementType: 'Collections',
        elementCategory: 'Table of Contents',
        elementName: 'Insight card - drag & drop',
        collectionName: eventProps.collectionName,
        insightName: eventProps.insightName,
        proposition: eventProps.proposition,
      });
    },
    [track]
  );

  const trackToCToggle = useCallback(
    async (eventProps: ToCInsightCardClickedAnalytics) => {
      track<ToCToggleTags>('Page Element Clicked', {
        ...(await getAccessTags()),
        ...collectionsDetailsPage,
        elementType: 'Collections',
        elementCategory: 'Table of Contents',
        elementName: `${eventProps.wasExpanded ? 'Collapse' : 'Expand'} Table of Contents`,
        collectionName: eventProps.collectionName,
      });
    },
    [track]
  );

  const trackInsightLinkClicked = useCallback(
    async (eventProps: InsightAnalytics & InsightPositionAnalytics) => {
      track('Page Element Clicked', {
        ...(await getAccessTags()),
        ...collectionsDetailsPage,
        elementType: 'Collections',
        elementCategory: 'Button',
        elementName: 'Drill through from insight',
        linkName: eventProps.insightName,
        collectionName: eventProps.collectionName,
        insightPosition: eventProps.insightPosition,
        proposition: eventProps.proposition,
        source: eventProps.source,
        chartType: eventProps.chartType,
      });
    },
    [track]
  );

  const trackInsightConfigItemsApplied = useCallback(
    async (eventProps: Omit<InsightAnalytics, 'chartType'> & InsightConfigItemsChanges) => {
      track('Filter Applied', {
        ...(await getAccessTags()),
        ...collectionsDetailsPage,
        elementType: 'Collections',
        elementCategory: 'Button',
        elementName: 'Edit Collection Insight Settings',
        ...eventProps,
      });
    },
    [track]
  );

  const trackEmptyCollectionsLinkClicked = useCallback(async () => {
    track('Page Element Clicked', {
      ...(await getAccessTags()),
      ...collectionsOverviewPage,
      elementType: 'Collections',
      elementCategory: 'Button',
      elementName: 'Learn more about Collections clicked',
    });
  }, [track]);

  const trackEditInsightSettingsButtonClicked = useCallback(
    async (eventProps: EditInsightSettingsAnalitycs) => {
      const { access } = eventProps.trackingTags ?? {};

      track<EditInsightSettingsTags>('Page Element Clicked', {
        access: access ?? '',
        ...collectionsDetailsPage,
        elementType: 'Collections',
        elementCategory: 'Button',
        elementName: 'Edit Collection Insight Settings',
        collectionName: eventProps.collectionName,
        collectionId: eventProps.collectionId,
        chartName: eventProps.chartName,
        source: eventProps.source,
        dataType: eventProps.dataType,
        insightName: eventProps.insightName,
        insightPosition: eventProps.insightPosition,
        proposition: eventProps.proposition,
      });
    },
    [track]
  );

  const trackSearchCollections = useCallback(async () => {
    track('Page Element Clicked', {
      ...(await getAccessTags()),
      ...collectionsOverviewPage,
      elementType: 'Collections',
      elementCategory: 'Button',
      elementName: 'Search Collections',
    });
  }, [track]);

  return {
    trackCollectionsPageViewed,
    trackCollectionDetailsPageViewed,
    trackCollectionClicked,
    trackCollectionDetailsPageElementClicked,
    trackCreateCollectionPageElementClicked,
    trackCollectionDetailsPageChartInteractions,
    trackInsightElementClicked,
    trackInsightViewed,
    trackTextBlockViewed,
    trackInsightError,
    trackAddInsightToCollectionButtonClicked,
    trackInsightAddedToCollection,
    trackCollectionsInfoModalClicked,
    trackInsightExportButtonClicked,
    trackInsightExportConfirmationButtonClicked,
    trackToCInsightCardClicked,
    trackInsightReordered,
    trackQuickActionsMenuItemClicked,
    trackToCToggle,
    trackInsightLinkClicked,
    trackEmptyCollectionsLinkClicked,
    trackTextBlockAddedToCollection,
    trackTextBlockInteractions,
    trackInsightConfigItemsApplied,
    trackEditInsightSettingsButtonClicked,
    trackSearchCollections,
  };
};
